import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Save, ShieldAlert } from "lucide-react";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";

const POLICY_FIELDS = [
  { key: "required_daily_digest", label: "Daily In-App Digest", hint: "Require an in-app summary. Each user can still turn email off or choose weekly email." },
  { key: "required_expired",      label: "Expired Items",    hint: "Always include the EXPIRED section in every recipient's digest." },
  { key: "required_today",        label: "Expires Today",    hint: "Always include the EXPIRES TODAY section." },
  { key: "required_3day",         label: "3-Day Warning",    hint: "Always include the EXPIRES IN 3 DAYS section." },
  { key: "required_7day",         label: "7-Day Warning",    hint: "Always include the EXPIRES IN 7 DAYS section." },
];

export default function NotificationPolicyPage() {
  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/notification-policy");
      setPolicy(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!policy) return;
    setSaving(true);
    try {
      const payload = Object.fromEntries(POLICY_FIELDS.map((f) => [f.key, policy[f.key]]));
      const { data } = await api.put("/admin/notification-policy", payload);
      setPolicy(data);
      toast.success("Notification policy saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !policy) {
    return (
      <div className="p-10 text-center" data-testid="notification-policy-loading">
        <Loader2 className="animate-spin mx-auto text-ink/40" size={28} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl" data-testid="admin-notification-policy-page">
      <PageHeader
        eyebrow="Communications"
        title="Notification Policy"
        subtitle={
          <>
            Mark digest items as <strong>required</strong>. Required items always appear in every user's daily digest,
            even if they've opted out — useful for safety-critical sections like EXPIRED inventory.
          </>
        }
      />

      <Card variant="raised" className="p-5 mb-4">
        <div className="flex items-center gap-3 mb-4">
          <ShieldAlert size={18} />
          <h2 className="font-display font-black text-lg tracking-tight">Required items</h2>
        </div>
        <div className="space-y-3">
          {POLICY_FIELDS.map((f) => (
            <label key={f.key} className="flex items-start gap-3 cursor-pointer" data-testid={`policy-row-${f.key}`}>
              <input
                type="checkbox"
                checked={!!policy[f.key]}
                onChange={(e) => setPolicy({ ...policy, [f.key]: e.target.checked })}
                className="mt-1 h-5 w-5 accent-brand"
                data-testid={`policy-checkbox-${f.key}`}
              />
              <div className="flex-1">
                <div className="font-display font-semibold text-sm">{f.label}</div>
                <div className="text-xs text-ink/60 mt-0.5">{f.hint}</div>
              </div>
            </label>
          ))}
        </div>
      </Card>

      <div className="flex gap-3">
        <Button
          tier="primary"
          onClick={save}
          disabled={saving}
          data-testid="save-policy-button"
        >
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
          {saving ? "Saving…" : "Save policy"}
        </Button>
      </div>
    </div>
  );
}
